import memoizedAuth from "@/lib/firebase/MemoizedAuth";
import type { JsonObject } from "@/types/adminV2Json";
import { downloadCsv } from "@/utils/csvExport";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
} from "@/utils/logging/correlationId";

const extractFilename = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) {
    return null;
  }

  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1] ?? null;
};

const downloadTextFile = (args: {
  content: string;
  filename: string;
  mimeType: string;
}): void => {
  const blob = new Blob([args.content], { type: args.mimeType });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", args.filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export type CsvDownloadErrorPayload = Record<string, unknown> & {
  message?: unknown;
};

export class CsvDownloadError extends Error {
  readonly status: number;
  readonly payload: CsvDownloadErrorPayload | null;

  constructor(args: {
    message: string;
    status: number;
    payload?: CsvDownloadErrorPayload | null;
  }) {
    super(args.message);
    this.name = "CsvDownloadError";
    this.status = args.status;
    this.payload = args.payload ?? null;
  }
}

const fetchAuthenticatedTextFile = async (args: {
  url: string;
  fallbackFilename: string;
  jsonBody?: JsonObject;
}): Promise<{ content: string; filename: string }> => {
  const currentUser = memoizedAuth.getCurrentUser();
  if (!currentUser) {
    throw new Error("Not authenticated");
  }

  const idToken = await currentUser.getIdToken();
  const correlationId = generateCorrelationId();

  const response = await fetch(args.url, {
    method: args.jsonBody ? "POST" : "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
      [CORRELATION_ID_HEADER]: correlationId,
      ...(args.jsonBody ? { "Content-Type": "application/json" } : {}),
    },
    ...(args.jsonBody ? { body: JSON.stringify(args.jsonBody) } : {}),
  });

  if (!response.ok) {
    const contentType = response.headers.get("Content-Type") ?? "";
    if (contentType.includes("application/json")) {
      const data = (await response
        .json()
        .catch(() => null)) as CsvDownloadErrorPayload | null;
      const message = typeof data?.message === "string" ? data.message : null;

      throw new CsvDownloadError({
        message: message ?? `Export failed (HTTP ${response.status})`,
        status: response.status,
        payload: data,
      });
    }

    throw new CsvDownloadError({
      message: `Export failed (HTTP ${response.status})`,
      status: response.status,
      payload: {
        errorCode: response.headers.get("x-error-code") ?? "export_failed",
        correlationId:
          response.headers.get(CORRELATION_ID_HEADER) ?? correlationId,
      },
    });
  }

  const contentDisposition = response.headers.get("Content-Disposition");
  const filename = extractFilename(contentDisposition) ?? args.fallbackFilename;
  const content = await response.text();

  return { content, filename };
};

export const downloadAuthenticatedCsv = async (args: {
  url: string;
  fallbackFilename: string;
  jsonBody?: JsonObject;
}): Promise<void> => {
  const { content, filename } = await fetchAuthenticatedTextFile(args);
  downloadCsv(content, filename);
};

export const downloadAuthenticatedTextFile = async (args: {
  url: string;
  fallbackFilename: string;
  mimeType?: string;
}): Promise<void> => {
  const { content, filename } = await fetchAuthenticatedTextFile(args);
  downloadTextFile({
    content,
    filename,
    mimeType: args.mimeType ?? "text/plain;charset=utf-8",
  });
};
